/**
 * Overview Section Component
 * POV-0060: Migrate existing Overview content into drawer section
 * 
 * Displays core part identification data:
 * - Part Number (HPN) with copy functionality
 * - Description / Part Name
 * - Status badge
 * - Classification (Main Group, Subgroup)
 * - Manufacturer Reference (Manufacturer, MPN)
 * - Unit of Measure
 * - Category / Subcategory
 * - Created / Updated dates
 * 
 * All timestamps displayed in Indian Standard Time (IST - UTC+5:30)
 */
import React, { useState, useCallback } from 'react';
import { Copy, Check, Package, Tag, Factory, Calendar, Hash, Layers, IndianRupee, Scale, Pencil, X, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { ViewRelatedHistoryLink } from './HistorySection';
import { formatISTDate, formatIST } from '../../utils/timezone';
import { toast } from 'sonner';
import fetchWithAuth from '../../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');

/**
 * Format number in Indian numbering system (lakhs, crores)
 * e.g., 1234567.89 → "12,34,567.89"
 */
const formatINR = (value) => {
  if (value == null || value === '' || isNaN(value)) return null;
  return Number(value).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const WEIGHT_UNITS = ['g', 'kg', 'tonnes', 'lbs', 'oz'];

/**
 * Status badge component with appropriate styling
 */
const StatusBadge = ({ status }) => {
  const statusConfig = {
    active: { label: 'Active', className: 'bg-green-100 text-green-700 border-green-200' },
    deleted: { label: 'Archived', className: 'bg-red-100 text-red-700 border-red-200' },
    draft: { label: 'Draft', className: 'bg-amber-100 text-amber-700 border-amber-200' },
    obsolete: { label: 'Obsolete', className: 'bg-slate-100 text-slate-600 border-slate-200' },
  };

  const config = statusConfig[status] || statusConfig.active;

  return (
    <span 
      className={cn(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border',
        config.className
      )}
      data-testid="overview-status-badge"
    >
      {config.label}
    </span>
  );
};

/**
 * Field display component for consistent styling
 */
const FieldDisplay = ({ label, value, icon: Icon, mono = false, className }) => (
  <div className={cn("space-y-1", className)}>
    <label className="flex items-center gap-1.5 text-xs text-slate-500 uppercase tracking-wider font-medium">
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </label>
    <p className={cn(
      "text-sm text-slate-900",
      mono && "font-mono",
      !value && "text-slate-400 italic"
    )}>
      {value || '—'}
    </p>
  </div>
);

/**
 * Section card for grouping related fields
 */
const SectionCard = ({ title, icon: Icon, children, className }) => (
  <div className={cn("bg-slate-50 rounded-lg p-4 border border-slate-100", className)}>
    {title && (
      <h4 className="flex items-center gap-2 text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {title}
      </h4>
    )}
    {children}
  </div>
);

/**
 * Inline editable cost field with ₹ INR formatting
 */
const InlineEditCost = ({ value, onSave, disabled }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    if (disabled) return;
    setEditValue(value != null ? String(value) : '');
    setEditing(true);
  };

  const handleSave = async () => {
    const parsed = parseFloat(editValue);
    if (editValue !== '' && (isNaN(parsed) || parsed < 0)) return;
    setSaving(true);
    await onSave(editValue === '' ? null : parsed);
    setSaving(false);
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-sm text-slate-400 font-mono">₹</span>
        <input
          type="number"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          autoFocus
          min="0"
          step="0.01"
          placeholder="0.00"
          className="w-full text-sm font-mono text-slate-900 bg-white border border-blue-300 rounded px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="inline-cost-input"
        />
        {saving && <Loader2 className="w-3 h-3 animate-spin text-blue-500" />}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "group flex items-center gap-1 rounded px-1 -mx-1 transition-colors",
        !disabled && "cursor-pointer hover:bg-slate-100"
      )}
      onClick={startEdit}
      data-testid="inline-cost-display"
    >
      <p className="text-sm font-mono text-emerald-700">
        {value != null ? `₹${formatINR(value)}` : <span className="text-slate-400 italic font-sans">— Set cost</span>}
      </p>
      {!disabled && (
        <Pencil className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </div>
  );
};

/**
 * Inline editable weight field with unit selector
 */
const InlineEditWeight = ({ value, unit, onSave, disabled }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [editUnit, setEditUnit] = useState('kg');
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    if (disabled) return;
    setEditValue(value != null ? String(value) : '');
    setEditUnit(unit || 'kg');
    setEditing(true);
  };

  const handleSave = async () => {
    const parsed = parseFloat(editValue);
    if (editValue !== '' && (isNaN(parsed) || parsed < 0)) return;
    setSaving(true);
    await onSave(editValue === '' ? null : parsed, editUnit);
    setSaving(false);
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          type="number"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          min="0"
          step="0.001"
          placeholder="0.000"
          className="flex-1 text-sm font-mono text-slate-900 bg-white border border-blue-300 rounded px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="inline-weight-input"
        />
        <select
          value={editUnit}
          onChange={(e) => setEditUnit(e.target.value)}
          className="text-xs bg-white border border-slate-300 rounded px-1.5 py-1 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="inline-weight-unit-select"
        >
          {WEIGHT_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
        <button onClick={handleSave} className="p-0.5 text-emerald-600 hover:bg-emerald-50 rounded" data-testid="inline-weight-save">
          <Check className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => setEditing(false)} className="p-0.5 text-slate-400 hover:bg-slate-100 rounded" data-testid="inline-weight-cancel">
          <X className="w-3.5 h-3.5" />
        </button>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-blue-500" />}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "group flex items-center gap-1 rounded px-1 -mx-1 transition-colors",
        !disabled && "cursor-pointer hover:bg-slate-100"
      )}
      onClick={startEdit}
      data-testid="inline-weight-display"
    >
      <p className="text-sm font-mono text-blue-700">
        {value != null ? `${value} ${unit || 'kg'}` : <span className="text-slate-400 italic font-sans">— Set weight</span>}
      </p>
      {!disabled && (
        <Pencil className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </div>
  );
};

/**
 * Main Overview Section Component
 */
const OverviewSection = ({ partData, onViewRelatedHistory, onPartUpdated }) => {
  const [copied, setCopied] = useState(false);

  // Copy HPN to clipboard
  const handleCopyHPN = async () => {
    if (!partData?.hpn_display) return;
    
    try {
      await navigator.clipboard.writeText(partData.hpn_display);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy HPN:', err);
    }
  };

  // Format date for display (IST)
  const formatDate = (dateString) => {
    if (!dateString) return null;
    return formatISTDate(dateString);
  };

  // Format datetime for display (IST)
  const formatDateTime = (dateString) => {
    if (!dateString) return null;
    return formatIST(dateString, { includeSeconds: false });
  };

  // Save cost/weight to backend
  const handleSaveCost = useCallback(async (newCost) => {
    if (!partData?.hpn_compact) return;
    try {
      const tk = getToken();
      const res = await fetchWithAuth(`${API}/api/v1/parts/by-hpn/${partData.hpn_compact}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ cost_inr: newCost, reason_code: 'data_correction', comment: 'Updated unit cost' }),
      });
      if (res.ok) {
        toast.success('Cost updated');
        onPartUpdated?.();
      } else {
        try { const err = await res.json(); toast.error(err.detail || 'Failed to update cost'); }
        catch { const text = await res.text(); toast.error(text || `Error ${res.status}`); }
      }
    } catch (e) { toast.error(e.message); }
  }, [partData?.hpn_compact, onPartUpdated]);

  const handleSaveWeight = useCallback(async (newValue, newUnit) => {
    if (!partData?.hpn_compact) return;
    try {
      const tk = getToken();
      const res = await fetchWithAuth(`${API}/api/v1/parts/by-hpn/${partData.hpn_compact}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ weight_value: newValue, weight_unit: newUnit, reason_code: 'data_correction', comment: 'Updated unit weight' }),
      });
      if (res.ok) {
        toast.success('Weight updated');
        onPartUpdated?.();
      } else {
        try { const err = await res.json(); toast.error(err.detail || 'Failed to update weight'); }
        catch { const text = await res.text(); toast.error(text || `Error ${res.status}`); }
      }
    } catch (e) { toast.error(e.message); }
  }, [partData?.hpn_compact, onPartUpdated]);

  if (!partData) {
    return (
      <div className="text-sm text-slate-500 italic" data-testid="overview-no-data">
        No part data available
      </div>
    );
  }

  const isArchived = partData.status === 'deleted';

  return (
    <div className="space-y-4" data-testid="overview-section-content">
      {/* Part Identifier Header */}
      <div className="flex items-start justify-between gap-4 pb-3 border-b border-slate-200">
        <div className="flex-1 min-w-0">
          {/* Part Number with Copy */}
          <div className="flex items-center gap-2 mb-1">
            <span 
              className="font-mono text-lg font-semibold text-slate-900 tracking-wide"
              data-testid="overview-part-number"
            >
              {partData.hpn_display || partData.hpn_compact || '—'}
            </span>
            <button
              onClick={handleCopyHPN}
              className={cn(
                "p-1 rounded transition-colors",
                copied 
                  ? "text-green-600 bg-green-50" 
                  : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              )}
              title={copied ? "Copied!" : "Copy Part Number"}
              data-testid="overview-copy-hpn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          
          {/* Description */}
          <p 
            className="text-sm text-slate-600 line-clamp-2"
            data-testid="overview-description"
          >
            {partData.description || 'No description provided'}
          </p>
        </div>
        
        {/* Status Badge */}
        <StatusBadge status={partData.status} />
      </div>

      {/* Classification Section */}
      <SectionCard title="Classification" icon={Layers}>
        <div className="grid grid-cols-2 gap-4">
          <FieldDisplay
            label="Main Group"
            value={
              partData.main_group_code 
                ? `${partData.main_group_code}${partData.main_group_name ? ` - ${partData.main_group_name}` : ''}`
                : null
            }
            icon={Tag}
            mono={!!partData.main_group_code && !partData.main_group_name}
          />
          <FieldDisplay
            label="Subgroup"
            value={
              partData.subgroup_code 
                ? `${partData.subgroup_code}${partData.subgroup_name ? ` - ${partData.subgroup_name}` : ''}`
                : null
            }
            icon={Tag}
            mono={!!partData.subgroup_code && !partData.subgroup_name}
          />
          {partData.category && (
            <FieldDisplay
              label="Category"
              value={partData.category}
            />
          )}
          {partData.subcategory && (
            <FieldDisplay
              label="Subcategory"
              value={partData.subcategory}
            />
          )}
        </div>
      </SectionCard>

      {/* Manufacturer Reference Section - Only show if manufacturer data exists */}
      {(partData.manufacturer_name || partData.mpn) && (
        <SectionCard title="Manufacturer Reference" icon={Factory}>
          <div className="grid grid-cols-2 gap-4">
            <FieldDisplay
              label="Manufacturer"
              value={partData.manufacturer_name}
            />
            <FieldDisplay
              label="MPN"
              value={partData.mpn}
              mono
            />
          </div>
        </SectionCard>
      )}

      {/* Cost & Weight Section */}
      <SectionCard title="Cost & Weight" icon={IndianRupee}>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1" data-testid="overview-cost-field">
            <label className="flex items-center gap-1.5 text-xs text-slate-500 uppercase tracking-wider font-medium">
              <IndianRupee className="w-3 h-3" />
              Unit Cost (₹)
            </label>
            <InlineEditCost
              value={partData.cost_inr}
              onSave={handleSaveCost}
              disabled={isArchived}
            />
          </div>
          <div className="space-y-1" data-testid="overview-weight-field">
            <label className="flex items-center gap-1.5 text-xs text-slate-500 uppercase tracking-wider font-medium">
              <Scale className="w-3 h-3" />
              Unit Weight
            </label>
            <InlineEditWeight
              value={partData.weight_value}
              unit={partData.weight_unit}
              onSave={handleSaveWeight}
              disabled={isArchived}
            />
          </div>
        </div>
      </SectionCard>

      {/* Details Section */}
      <SectionCard title="Details" icon={Package}>
        <div className="grid grid-cols-2 gap-4">
          <FieldDisplay
            label="Unit of Measure"
            value={partData.uom}
            icon={Hash}
          />
          <FieldDisplay
            label="Group Code"
            value={
              partData.main_group_code && partData.subgroup_code
                ? `${partData.main_group_code}:${partData.subgroup_code}`
                : null
            }
            mono
          />
          <FieldDisplay
            label="Created"
            value={formatDate(partData.created_at)}
            icon={Calendar}
          />
          <FieldDisplay
            label="Last Updated"
            value={formatDateTime(partData.updated_at)}
            icon={Calendar}
          />
        </div>
      </SectionCard>

      {/* Archived Banner - Show if part is deleted */}
      {partData.status === 'deleted' && (
        <div 
          className="p-3 bg-red-50 border border-red-200 rounded-lg"
          data-testid="overview-archived-banner"
        >
          <p className="text-sm text-red-700">
            <span className="font-medium">This part has been archived.</span>
            {' '}It won't appear in search results. Use the Restore action to reactivate.
          </p>
          {partData.deletion_reason && (
            <p className="text-xs text-red-600 mt-1">
              Reason: {partData.deletion_reason}
              {partData.deletion_comment && ` - ${partData.deletion_comment}`}
            </p>
          )}
        </div>
      )}

      {/* View Related History Link */}
      <ViewRelatedHistoryLink 
        sectionKey="overview" 
        onClick={onViewRelatedHistory} 
      />
    </div>
  );
};

export default OverviewSection;
